import * as analytics from '@opendoor/shared-fe/build/analytics';
import { showToast } from '@opendoor/bricks/complex/Toast';
import { Sentry } from '@opendoor/observability';
import { push } from 'connected-react-router';
import Cookies from 'js-cookie';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import * as api from 'src/api';
import { APIError } from 'src/api';
import { ErrorCode } from 'src/declarations/ErrorCode';
import { postDataToWebview } from 'src/helpers/webview';
import { addressEntryPath, errorPagePath, sellerGlobalPagePath } from 'src/routes/pathBuilders';

import { getCurrentSlug, getInApp, getPageName } from '../router/selectors';
import {
  CANCEL_MANUAL_ADDRESS_VERIFICATION,
  HANDLE_EXCEPTION,
  SEND_PASSWORD_SETUP_EMAIL,
  TO_MANUAL_ADDRESS_VERIFICATION,
  TRACK_EVENT,
} from './actionTypes';
import { flow as getFlow, leadToken as getLeadToken } from './selectors';

export type ErrorLevel = 'fatal' | 'error' | 'warn';

function* handleException({
  exception,
  code,
  level,
  metadata,
}: {
  exception: APIError;
  code: ErrorCode;
  level: ErrorLevel;
  metadata?: Record<string, unknown>;
}) {
  const slug = yield select(getCurrentSlug);
  const page = yield select(getPageName);
  const leadToken = yield select(getLeadToken);
  const fetchedFlow = yield select(getFlow);

  reportException(exception, code, level, leadToken, fetchedFlow.name, metadata);
  analytics.trackEvent('seller-flow', 'error', level, {
    slug,
    page,
    code,
    flow: fetchedFlow.name,
    seller_input_uuid: leadToken,
    error: `${exception.name}: ${exception.message}`,
  });

  switch (level) {
    case 'fatal': {
      analytics.trackEvent('flow-state', 'change', 'exception');
      const inApp = yield select(getInApp);
      if (inApp) {
        postDataToWebview({ eventName: 'error' });
        // native app does not currently handle error, so show the page anyway
      }
      // eslint-disable-next-line no-console
      console.info('caught exception, sending to error page', exception);
      if (code == 'lead-not-found') {
        window.location.href = addressEntryPath();
      } else {
        window.location.href = errorPagePath({ slug, page, code, leadToken });
      }
      break;
    }
    case 'error':
      showToast(
        'error',
        'Oops, something went wrong! Please try again.',
        'reception-fe-error-toast',
        {
          toastOptions: { autoClose: 7000 },
        },
      );
      break;

    case 'warn': // do nothing on warn errors
    default:
      break;
  }
}

function* handleExceptionAction(action: any) {
  yield handleException(action.payload);
}

function trackEvent(actionParam) {
  const { category, action, label, extra } = actionParam.payload;
  analytics.trackEvent(category, action, label, extra);
}

function* toManualAddressVerification() {
  analytics.trackEvent('inputting', 'address', 'manual', { from_existing: true });

  const leadToken = yield select(getLeadToken);

  yield put(push(sellerGlobalPagePath({ token: leadToken, page: 'address-manual' })));
}

export const maybeSetLeadCookie = (lead: { token: string }) => {
  if (!lead.token) {
    return;
  }
  // For real staging and production, we need the cookie to be accessible
  // across all subdomains
  // i.e. ".domain.com" cookies are available on all subdomains, vs "www.domain.com"
  // which is only available on same-origin
  // https://www.mxsasha.eu/blog/2014/03/04/definitive-guide-to-cookie-domains/
  let cookieDomain = window.location.hostname;
  if (cookieDomain.indexOf('opendoor.com') !== -1) {
    cookieDomain = '.opendoor.com';
  } else if (window.location.hostname.indexOf('simplersell.com') !== -1) {
    cookieDomain = '.simplersell.com';
  }
  const expires = 365 * 5; // days
  Cookies.set('lead_token', lead.token, { expires, domain: cookieDomain });
};

function* cancelManualAddressVerification(action) {
  const hadInitialAddress = action.payload.hadInitialAddress;
  analytics.trackEvent('modify-address', 'cancel', undefined, {
    from_existing: hadInitialAddress,
  });
  if (hadInitialAddress) {
    const leadToken = yield select(getLeadToken);
    yield put(push(`/seller-lead/${leadToken}`));
  } else {
    window.history.back();
    setTimeout(() => {
      window.location.href = 'https://www.opendoor.com';
    }, 1000);
  }
}

function* sendPasswordSetUpEmail(action: any) {
  const { email, redirectUrl } = action.payload;
  try {
    yield call(api.sendPasswordSetUpEmail, email, redirectUrl);
  } catch (exception) {
    yield handleException({ exception, code: 'send-password-setup-email', level: 'warn' });
  }
}

function reportException(
  exception: Error,
  action: string,
  level: string,
  leadToken: string,
  flow: string,
  metadata?: Record<string, unknown>,
) {
  const extras: { [key: string]: unknown } = { ...metadata, seller_input_uuid: leadToken };
  const tags: { [key: string]: string } = {
    action,
    level,
    seller_input_uuid: leadToken,
    flow,
  };

  Sentry.withScope((scope) => {
    scope.setTags(tags);
    scope.setExtras(extras);
    Sentry.captureException(exception);
  });
}

export default function* saga() {
  yield all([
    takeEvery(TRACK_EVENT, trackEvent),
    takeEvery(HANDLE_EXCEPTION, handleExceptionAction),
    takeEvery(CANCEL_MANUAL_ADDRESS_VERIFICATION, cancelManualAddressVerification),
    takeLatest(TO_MANUAL_ADDRESS_VERIFICATION, toManualAddressVerification),
    takeLatest(SEND_PASSWORD_SETUP_EMAIL, sendPasswordSetUpEmail),
  ]);
}
